import * as THREE from "three";
import { ENVIRONMENT_CONFIG, RESPAWN_ZONE_CONFIG, COLORS } from "./config";
import { Tank } from "./Tank";
import { EnvironmentService } from "./services/EnvironmentService";
import {
  ChunkData,
  EnvironmentElement,
  EnvironmentElementType,
  AirplaneEnvironmentElement,
} from "../../../shared/src/environment.types";
import { handleTankCollision } from "./damage";
import { GrassRenderer } from "./GrassRenderer";
import { SkyBox } from "./SkyBox";
import { BillboardRenderer } from "./ads/BillboardRenderer";
import { AirplaneRenderer } from "./ads/AirplaneRenderer";
import { EventManager } from "./EventManager";
import { createTent, createPaperMaterial, addEdges, addBarycentricCoordinates } from "./utils/papercraft";

console.log("[environment.ts] Imported addBarycentricCoordinates:", !!addBarycentricCoordinates);

/**
 * Tworzy środowisko gry - teren, drzewa, skały, itp.
 * @param scene Scena Three.js
 * @returns Obiekt zawierający referencje do elementów środowiska
 */
export interface Environment {
  ground: THREE.Mesh;
  rocks: THREE.Mesh[];
  trees: THREE.Group[];
  mudPatches: THREE.Mesh[];
  tanks: Tank[];
  respawnZone: THREE.Mesh;
  scene: THREE.Scene;
  environmentService: EnvironmentService;
  grassRenderer: GrassRenderer;
  skybox: SkyBox;
  billboardRenderer: BillboardRenderer;
  airplaneRenderer: AirplaneRenderer;
  addTank: (tank: Tank) => void;
  removeTank: (tank: Tank) => void;
  updateChunks: (position: THREE.Vector3) => void;
  getRandomRespawnPoint: () => { position: THREE.Vector3; rotation: number };
  update: () => void;
}

function hashString(input: string, seed: number): number {
  let hash = seed;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function hashNumber(value: number, seed: number): number {
  const normalized = Math.round(value * 1000);
  return hashString(normalized.toString(), seed);
}

function getChunkFingerprint(chunks: ChunkData[]): number {
  let hash = 2166136261;
  for (const chunk of chunks) {
    hash = hashString(chunk.chunkId, hash);
    hash = hashString(chunk.elements.length.toString(), hash);
    for (const element of chunk.elements) {
      hash = hashString(element.id, hash);
      hash = hashString(element.type ?? "unknown", hash);
      hash = hashNumber(element.position.x, hash);
      hash = hashNumber(element.position.y, hash);
      hash = hashNumber(element.position.z, hash);
      hash = hashNumber(element.rotation.x, hash);
      hash = hashNumber(element.rotation.y, hash);
      hash = hashNumber(element.rotation.z, hash);
      hash = hashNumber(element.scale, hash);
    }
  }
  return hash >>> 0;
}

function disposeObjectResources(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return;
    }

    // Perf: shared geometries/materials are marked and must survive chunk rebuilds.
    if (!child.geometry?.userData.isShared) {
      child.geometry?.dispose();
    }

    if (Array.isArray(child.material)) {
      child.material.forEach((material) => {
        if (material.userData.isShared) {
          return;
        }
        if ((material as THREE.MeshStandardMaterial).map) {
          (material as THREE.MeshStandardMaterial).map?.dispose();
        }
        material.dispose();
      });
      return;
    }

    if (!child.material?.userData.isShared) {
      if ((child.material as THREE.MeshStandardMaterial).map) {
        (child.material as THREE.MeshStandardMaterial).map?.dispose();
      }
      child.material?.dispose();
    }
  });
}

// Perf: chunk rebuilds created a new geometry + material per rock/mud/tree,
// i.e. hundreds of GPU uploads and draw-state changes. These are shared
// (unit-sized, scaled per instance) - visuals are identical.
let sharedRockGeometry: THREE.DodecahedronGeometry | null = null;
let sharedRockMaterial: THREE.MeshStandardMaterial | null = null;
let sharedMudGeometry: THREE.CircleGeometry | null = null;
let sharedMudMaterial: THREE.MeshStandardMaterial | null = null;
let sharedTrunkGeometry: THREE.BufferGeometry | null = null;
let sharedCrownGeometry: THREE.BufferGeometry | null = null;

function getSharedRockGeometry(): THREE.DodecahedronGeometry {
  if (!sharedRockGeometry) {
    sharedRockGeometry = new THREE.DodecahedronGeometry(0.5, 1);
    sharedRockGeometry.userData.isShared = true;
  }
  return sharedRockGeometry;
}

function getSharedRockMaterial(): THREE.MeshStandardMaterial {
  if (!sharedRockMaterial) {
    sharedRockMaterial = new THREE.MeshStandardMaterial({
      color: 0x888888,
      roughness: 0.9,
      metalness: 0.1,
      fog: true,
    });
    sharedRockMaterial.userData.isShared = true;
  }
  return sharedRockMaterial;
}

function getSharedMudAssets(): { geometry: THREE.CircleGeometry; material: THREE.MeshStandardMaterial } {
  if (!sharedMudGeometry) {
    sharedMudGeometry = new THREE.CircleGeometry(1, 16);
    sharedMudGeometry.userData.isShared = true;
  }
  if (!sharedMudMaterial) {
    sharedMudMaterial = new THREE.MeshStandardMaterial({
      color: 0x5d4037,
      roughness: 0.7,
      metalness: 0.1,
      fog: true,
      transparent: true,
      opacity: 0.85,
    });
    sharedMudMaterial.userData.isShared = true;
  }
  return { geometry: sharedMudGeometry, material: sharedMudMaterial };
}

function getSharedTrunkGeometry(): THREE.BufferGeometry {
  if (!sharedTrunkGeometry) {
    let trunk = new THREE.CylinderGeometry(0.2, 0.25, 1.8, 6) as THREE.BufferGeometry;
    trunk = trunk.toNonIndexed();
    trunk.computeVertexNormals();
    addBarycentricCoordinates(trunk);
    trunk.userData.isShared = true;
    sharedTrunkGeometry = trunk;
  }
  return sharedTrunkGeometry;
}

function getSharedCrownGeometry(): THREE.BufferGeometry {
  if (!sharedCrownGeometry) {
    let crown = new THREE.ConeGeometry(1.1, 2.2, 7) as THREE.BufferGeometry;
    crown = crown.toNonIndexed();
    crown.computeVertexNormals();
    addBarycentricCoordinates(crown);
    crown.userData.isShared = true;
    sharedCrownGeometry = crown;
  }
  return sharedCrownGeometry;
}

/**
 * Tworzy środowisko gry - teren, drzewa, skały, itp.
 * @param scene Scena Three.js
 * @param camera Kamera używana w grze
 * @param environmentService Serwis do komunikacji z serwerem
 * @returns Obiekt zawierający referencje do elementów środowiska
 */
export function createEnvironment(
  scene: THREE.Scene,
  camera: THREE.Camera,
  environmentService: EnvironmentService,
): Environment {
  console.log("createEnvironment - start");

  // Utworzenie podłoża
  console.log("Tworzenie podłoża");
  const groundGeometry = new THREE.PlaneGeometry(400, 400);
  const groundMaterial = new THREE.MeshStandardMaterial({
    color: 0x7cac2e,
    roughness: 0.8,
    metalness: 0.2,
    fog: true, // Upewniamy się, że podłoże reaguje na mgłę
  });
  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  // Inicjalizacja kolekcji obiektów środowiska
  const rocks: THREE.Mesh[] = [];
  const trees: THREE.Group[] = [];
  const mudPatches: THREE.Mesh[] = [];
  const tanks: Tank[] = [];

  // Inicjalizacja renderera trawy
  const grassRenderer = new GrassRenderer(scene);
  grassRenderer.setCamera(camera); // Przekazujemy kamerę do GrassRenderer

  // Inicjalizacja skyboxa z chmurami
  const skybox = new SkyBox(scene, ENVIRONMENT_CONFIG.SKY);

  // Inicjalizacja renderera billboardów
  const billboardRenderer = new BillboardRenderer(scene);

  const airplaneRenderer = new AirplaneRenderer(scene);

  // Dodanie strefy respawnu
  const respawnZoneGeometry = new THREE.PlaneGeometry(RESPAWN_ZONE_CONFIG.SIZE, RESPAWN_ZONE_CONFIG.SIZE);
  const respawnZoneMaterial = new THREE.MeshBasicMaterial({
    color: 0x00ff00, // Zielony kolor
    transparent: true,
    opacity: 0.2,
    side: THREE.DoubleSide,
    depthWrite: false, // Zapobiega problemom z przezroczystością
    fog: true, // Upewniamy się, że strefa respawnu reaguje na mgłę
  });
  const respawnZone = new THREE.Mesh(respawnZoneGeometry, respawnZoneMaterial);
  respawnZone.name = "respawnZone"; // Dodaj nazwę do identyfikacji
  respawnZone.rotation.x = -Math.PI / 2;
  respawnZone.position.y = 0.1;
  //respawnZone.position.set(-40, 0.1, -40); // Ustawiamy strefę respawnu w rogu mapy
  scene.add(respawnZone);
  const respawnPulseStartTime = performance.now();


  console.log("createEnvironment - zakończone");

  // Słuchacz na otrzymanie nowych chunków z EventManager
  const lastChunkHashRef = { value: -1 };

  EventManager.addListener("environment", document, "environment:chunksReceived", ((
    event: CustomEvent<{ chunks: ChunkData[] }>,
  ) => {
    const chunks = event.detail.chunks;

    const newHash = getChunkFingerprint(chunks);
    if (newHash === lastChunkHashRef.value) {
      console.log("[Environment] Skipping chunk rebuild – data unchanged");
      return;
    }
    lastChunkHashRef.value = newHash;

    // Czyścimy stare obiekty (geometrie/materiały są współdzielone - nie disposal)
    rocks.forEach((rock) => {
      scene.remove(rock);
    });
    trees.forEach((tree) => {
      scene.remove(tree);
    });
    mudPatches.forEach((mud) => {
      scene.remove(mud);
    });
    rocks.length = 0;
    trees.length = 0;
    mudPatches.length = 0;

    // Czyścimy starą trawę
    grassRenderer.clearAllGrass();

    // Czyścimy stare billboardy
    billboardRenderer.clearAllBillboards();
    airplaneRenderer.clearAllAirplanes();

    // Liczniki elementów
    let rockCount = 0;
    let treeCount = 0;
    let mudCount = 0;
    let grassCount = 0;
    let billboardCount = 0;

    // Dodajemy nowe obiekty z otrzymanych chunków
    chunks.forEach((chunk) => {
      chunk.elements.forEach((element) => {
        if (!element.type) {
          // Sprawdź dokładnie strukturę elementu
          console.log(`[Environment] Sprawdzanie elementu bez typu: ${JSON.stringify(element)}`);

          // Jeśli to element samolotu (wykrywanie po ID)
          if (element.id && typeof element.id === "string" && element.id.includes("airplane")) {
            console.log(`[Environment] Dodaję typ dla samolotu: ${element.id}`);
            element.type = EnvironmentElementType.AIRPLANE;
          } else {
            console.warn(`[Environment] Pominięto element bez typu: ${JSON.stringify(element)}`);
            return; // Przejście do następnej iteracji
          }
        }

        switch (element.type) {
          case EnvironmentElementType.ROCK:
            const rock = createRock(element);
            scene.add(rock);
            rocks.push(rock);
            rockCount++;
            break;
          case EnvironmentElementType.TREE:
            const tree = createTree();
            tree.position.copy(element.position);
            tree.rotation.copy(element.rotation);
            tree.scale.setScalar(element.scale);
            tree.updateMatrix();
            tree.updateMatrixWorld(true);
            scene.add(tree);
            trees.push(tree);
            treeCount++;
            break;
          case EnvironmentElementType.GRASS:
            // Obsługa trawy przez dedykowany renderer
            grassRenderer.createOrUpdateGrassPatch(element);
            grassCount++;
            break;
          case EnvironmentElementType.MUD:
            const mud = createMudPatch(element);
            scene.add(mud);
            mudPatches.push(mud);
            mudCount++;
            break;
          case EnvironmentElementType.BILLBOARD:
            // Obsługa billboardów przez dedykowany renderer
            billboardRenderer.createOrUpdateBillboard(element);
            billboardCount++;
            break;
          case EnvironmentElementType.AIRPLANE:
            // Obsługa samolotów przez dedykowany renderer
            airplaneRenderer.addAirplaneFromElement(element as AirplaneEnvironmentElement);
            break;
          default:
            console.warn(`[Environment] Nieznany typ elementu: ${element.type}`);
        }
      });
    });
  }) as EventListener);

  // Funkcja aktualizująca środowisko w każdej klatce
  const update = () => {
    const respawnPulseTime = performance.now() - respawnPulseStartTime;
    respawnZoneMaterial.opacity = 0.1 + Math.sin(respawnPulseTime * 0.002) * 0.1;

    // Aktualizacja animacji trawy
    grassRenderer.update();

    // Aktualizacja skyboxa i chmur
    skybox.update(1000 / 60); // Przekazujemy przybliżony czas klatki (16.67ms)

    // Aktualizacja pozycji czołgów dla interakcji z trawą
    tanks.forEach((tank) => {
      if (tank && tank.mesh) {
        // Używamy playerId jako identyfikatora czołgu zgodnie z systemem identyfikacji graczy
        grassRenderer.updatePlayerPosition(tank.playerId, tank.mesh.position);
      }
    });

    // Aktualizacja złamanych drzew
    updateBrokenTrees(trees);

    // Aktualizuj samoloty
    airplaneRenderer.update();
  };

  const environment: Environment = {
    ground,
    rocks,
    trees,
    mudPatches,
    tanks,
    respawnZone,
    scene,
    environmentService,
    grassRenderer,
    skybox,
    billboardRenderer,
    airplaneRenderer,
    addTank: (tank: Tank) => {
      if (!tanks.includes(tank)) {
        console.log("Adding tank to environment:", tank.playerId);
        tanks.push(tank);
      }
    },
    removeTank: (tank: Tank) => {
      const index = tanks.indexOf(tank);
      if (index !== -1) {
        console.log("Removing tank from environment:", tank.playerId);
        tank.dispose(); // Call dispose to clean up resources
        tanks.splice(index, 1);
      }
    },
    updateChunks: (position: THREE.Vector3) => {
      console.log("Requesting chunks update for position:", position);
      environmentService.requestChunks({
        x: position.x,
        z: position.z,
      });
    },
    getRandomRespawnPoint: () => {
      const zonePosition = respawnZone.position;
      const zoneSize = RESPAWN_ZONE_CONFIG.SIZE;
      const halfZone = zoneSize / 2;
      const tankRadius = ENVIRONMENT_CONFIG.COLLISION.TANK_RADIUS;
      const safePadding = 1.5;
      const maxAttempts = 40;

      const isPositionClear = (x: number, z: number): boolean => {
        for (const tree of trees) {
          const treeRadius = 0.3 * tree.scale.x + tankRadius + safePadding;
          const dx = x - tree.position.x;
          const dz = z - tree.position.z;
          if (dx * dx + dz * dz < treeRadius * treeRadius) {
            return false;
          }
        }

        for (const rock of rocks) {
          const rockRadius = 0.5 * Math.max(rock.scale.x, rock.scale.z) + tankRadius + safePadding;
          const dx = x - rock.position.x;
          const dz = z - rock.position.z;
          if (dx * dx + dz * dz < rockRadius * rockRadius) {
            return false;
          }
        }

        return true;
      };

      let randomX = zonePosition.x;
      let randomZ = zonePosition.z;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        randomX = zonePosition.x + (Math.random() - 0.5) * zoneSize;
        randomZ = zonePosition.z + (Math.random() - 0.5) * zoneSize;

        if (isPositionClear(randomX, randomZ)) {
          break;
        }

        const angle = (attempt / maxAttempts) * Math.PI * 2;
        const radius = Math.min(halfZone - tankRadius, 2 + attempt * 0.3);
        randomX = zonePosition.x + Math.cos(angle) * radius;
        randomZ = zonePosition.z + Math.sin(angle) * radius;

        if (isPositionClear(randomX, randomZ)) {
          break;
        }
      }

      return {
        position: new THREE.Vector3(randomX, 0, randomZ),
        rotation: Math.random() * Math.PI * 2,
      };
    },
    update, // Dodajemy nową metodę do obiektu środowiska
  };

  return environment;
}

// Nowe funkcje pomocnicze do tworzenia obiektów
function createRock(element: EnvironmentElement): THREE.Mesh {
  const rock = new THREE.Mesh(getSharedRockGeometry(), getSharedRockMaterial());

  rock.position.copy(element.position);
  rock.rotation.copy(element.rotation);
  rock.scale.setScalar(element.scale);

  rock.castShadow = true;
  rock.receiveShadow = true;
  // Static object optimization - disable automatic matrix updates
  rock.matrixAutoUpdate = false;
  rock.updateMatrix();

  return rock;
}

function createMudPatch(element: EnvironmentElement): THREE.Mesh {
  const { geometry: mudGeometry, material: mudMaterial } = getSharedMudAssets();
  const mud = new THREE.Mesh(mudGeometry, mudMaterial);

  mud.position.copy(element.position);
  mud.position.y += 0.02; // Podnieś niewielką wartość nad poziom trawy
  mud.rotation.copy(element.rotation);
  // Perf: shared unit circle scaled per instance - identical visuals to per-patch geometry.
  mud.scale.setScalar(element.scale);

  // wyższy renderOrder dla błota
  mud.renderOrder = 1;
  mud.receiveShadow = true;
  // Static object optimization - disable automatic matrix updates
  mud.matrixAutoUpdate = false;
  mud.updateMatrix();

  return mud;
}

/**
 * Tworzy model drzewa w stylu "papercraft" (uproszczony).
 * Zmodyfikowana wersja oryginalnej funkcji.
 * Nadal zwraca pojedynczą grupę Three.js dla jednego drzewa.
 * Lepsza wydajność niż oryginał dzięki MeshLambertMaterial, ale
 * gorsza niż InstancedMesh przy dużej liczbie drzew.
 *
 * @returns {THREE.Group} Grupa Three.js zawierająca model drzewa.
 */
function createTree(): THREE.Group {
  const tree = new THREE.Group();

  // --- Parametry Geometrii (muszą odpowiadać współdzielonym geometriom powyżej) ---
  const trunkHeight = 1.8;
  const crownHeight = 2.2;

  // --- Pień (współdzielona geometria - identyczny wygląd, zero alokacji per drzewo) ---
  const trunkGeometry = getSharedTrunkGeometry();

  // Użyj nowego materiału shaderowego
  const trunkMaterial = createPaperMaterial(
    COLORS.treeTrunk,
    true, // Użyj shadera
    COLORS.trunkOutline, // Kolor konturu
    1.5, // Szerokość konturu (eksperymentuj)
  );
  const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
  trunk.position.y = trunkHeight / 2;
  trunk.castShadow = true;
  trunk.receiveShadow = true;
  tree.add(trunk);

  // --- Korona drzewa (współdzielona geometria) ---
  const crownGeometry = getSharedCrownGeometry();

  const crownMaterial = createPaperMaterial(
    COLORS.treeLeaves,
    true, // Użyj shadera
    COLORS.leavesOutline, // Kolor konturu
    1.5, // Szerokość konturu (eksperymentuj)
  );
  const crown = new THREE.Mesh(crownGeometry, crownMaterial);
  crown.position.y = trunkHeight + crownHeight / 2;
  crown.castShadow = true;
  tree.add(crown);

  // Static object optimization - disable automatic matrix updates for tree group
  tree.matrixAutoUpdate = false;
  tree.updateMatrix();

  return tree;
}

/**
 * Sprawdza, czy podana pozycja znajduje się w błocie
 * @param position Pozycja do sprawdzenia
 * @param mudPatches Tablica obiektów reprezentujących błoto
 * @returns true jeśli pozycja jest w błocie, false w przeciwnym razie
 */
export function isInMud(position: THREE.Vector3, mudPatches: THREE.Mesh[]): boolean {
  // Promień obszaru błota
  const mudRadius = ENVIRONMENT_CONFIG.MUD.RADIUS;
  const mudRadiusSq = mudRadius * mudRadius;

  // Sprawdź odległość od każdego obszaru błota
  for (const mud of mudPatches) {
    const dx = position.x - mud.position.x;
    const dz = position.z - mud.position.z;
    if (dx * dx + dz * dz < mudRadiusSq) {
      return true;
    }
  }

  return false;
}

/**
 * Sprawdza, czy podana pozycja koliduje z kamieniami
 * @param position Pozycja do sprawdzenia
 * @param rocks Tablica obiektów reprezentujących kamienie
 * @returns true jeśli występuje kolizja, false w przeciwnym razie
 */
export function isCollidingWithRocks(position: THREE.Vector3, rocks: THREE.Mesh[]): boolean {
  if (!rocks || rocks.length === 0) {
    //console.log("[isCollidingWithRocks] No rocks to check collisions with");
    return false;
  }

  // Promień kolizji czołgu
  const tankRadius = ENVIRONMENT_CONFIG.COLLISION.TANK_RADIUS;

  // Sprawdź odległość od każdego kamienia
  for (const rock of rocks) {
    // Oblicz rzeczywisty promień kamienia (uwzględniając skalę)
    const rockScale = Math.max(rock.scale.x, rock.scale.z);
    const rockRadius = 0.5 * rockScale; // Bazowy promień * skala

    // Minimalna odległość to suma promieni
    const minDistance = tankRadius + rockRadius;

    // Sprawdź tylko odległość w płaszczyźnie X-Z (ignoruj Y)
    const dx = position.x - rock.position.x;
    const dz = position.z - rock.position.z;
    if (dx * dx + dz * dz < minDistance * minDistance) {
      return true;
    }
  }

  return false;
}

/**
 * Sprawdza, czy podana pozycja koliduje z drzewami
 * @param position Pozycja do sprawdzenia
 * @param trees Tablica obiektów reprezentujących drzewa
 * @returns Obiekt z informacją o kolizji i referencją do drzewa, z którym nastąpiła kolizja
 */
export function isCollidingWithTrees(
  position: THREE.Vector3,
  trees: THREE.Group[],
): { colliding: boolean; tree: THREE.Group | null } {
  if (!trees || trees.length === 0) {
    //console.log("[isCollidingWithTrees] No trees to check collisions with");
    return { colliding: false, tree: null };
  }

  // Promień kolizji czołgu
  const tankRadius = ENVIRONMENT_CONFIG.COLLISION.TANK_RADIUS;

  // Sprawdź odległość od każdego drzewa
  for (const tree of trees) {
    // Promień pnia drzewa (uwzględniając skalę)
    const treeScale = tree.scale.x;
    const trunkRadius = 0.3 * treeScale; // Bazowy promień pnia * skala

    // Minimalna odległość to suma promieni
    const minDistance = tankRadius + trunkRadius;

    // Sprawdź tylko odległość w płaszczyźnie X-Z (ignoruj Y)
    const dx = position.x - tree.position.x;
    const dz = position.z - tree.position.z;
    if (dx * dx + dz * dz < minDistance * minDistance) {
      return { colliding: true, tree: tree };
    }
  }

  return { colliding: false, tree: null };
}

/**
 * Łamie drzewo w wyniku kolizji z czołgiem
 * @param tree Drzewo do złamania
 * @param scene Scena, na której znajduje się drzewo
 * @param impactDirection Kierunek uderzenia
 */
export function breakTree(tree: THREE.Group, scene: THREE.Scene, impactDirection: THREE.Vector3): void {
  // Sprawdź, czy drzewo jest już złamane
  if ((tree as any).isBroken) {
    return;
  }

  // Oznacz drzewo jako złamane
  (tree as any).isBroken = true;
  (tree as any).fallStartTime = Date.now();

  // Zapisz oryginalną rotację drzewa
  (tree as any).originalRotation = tree.rotation.clone();

  // Kierunek upadku drzewa (przeciwny do kierunku uderzenia)
  const fallDirection = impactDirection.clone().normalize();
  (tree as any).fallDirection = fallDirection;

  // Dodaj efekt dźwiękowy łamania drzewa
  if (ENVIRONMENT_CONFIG.TREE.BREAKING_SOUND_ENABLED) {
    // Tutaj można dodać odtwarzanie dźwięku
    console.log("Dźwięk łamania drzewa!");
  }
}

/**
 * Aktualizuje stan złamanych drzew (animacja upadku)
 * @param trees Tablica drzew
 */
const _brokenTreeAxis = new THREE.Vector3();

export function updateBrokenTrees(trees: THREE.Group[]): void {
  const now = Date.now();
  const fallDuration = ENVIRONMENT_CONFIG.TREE.FALL_DURATION; // Czas trwania animacji upadku

  for (const tree of trees) {
    if ((tree as any).isBroken) {
      const elapsed = now - (tree as any).fallStartTime;
      const progress = Math.min(1, elapsed / fallDuration);

      // Maksymalny kąt przechylenia (90 stopni = leży na ziemi)
      const maxAngle = Math.PI / 2;

      // Oblicz aktualny kąt przechylenia
      const currentAngle = progress * maxAngle;

      // Kierunek upadku
      const fallDirection = (tree as any).fallDirection;

      // Obrót drzewa w kierunku upadku
      const originalRotation = (tree as any).originalRotation;

      // Oblicz oś obrotu (prostopadła do kierunku upadku) - resue temp, zero alokacji
      const rotationAxis = _brokenTreeAxis.set(-fallDirection.z, 0, fallDirection.x).normalize();

      // Ustaw nową rotację drzewa
      tree.quaternion.setFromAxisAngle(rotationAxis, currentAngle);
      tree.updateMatrix();
      tree.updateMatrixWorld(true);

      // Jeśli animacja się zakończyła, oznacz drzewo jako całkowicie złamane
      if (progress >= 1) {
        (tree as any).isFullyBroken = true;
      }
    }
  }
}

/**
 * Sprawdza kolizje między czołgami i obsługuje ich efekty
 * @param tank Czołg do sprawdzenia
 * @param tanks Lista wszystkich czołgów
 * @param handleCollision Czy obsłużyć efekty kolizji (domyślnie true)
 * @returns Lista czołgów, z którymi nastąpiła kolizja
 */
export function checkTankCollisions(tank: Tank, tanks: Tank[], handleCollision: boolean = true): Tank[] {
  // Jeśli czołg jest zniszczony lub w czasie ochronnym, nie sprawdzaj kolizji
  if (tank.isDestroyed || tank.isInSafeTime()) {
    return [];
  }

  //switched off for now as bugged
  return [];

  const collidingTanks: Tank[] = [];
  const tankRadius = ENVIRONMENT_CONFIG.COLLISION.TANK_VS_TANK_RADIUS;

  for (const otherTank of tanks) {
    // Nie sprawdzaj kolizji z samym sobą, zniszczonymi czołgami i czołgami w czasie ochronnym
    if (otherTank === tank || otherTank.isDestroyed || otherTank.isInSafeTime()) {
      continue;
    }

    const distance = tank.mesh.position.distanceTo(otherTank.mesh.position);
    if (distance < tankRadius * 2) {
      collidingTanks.push(otherTank);

      if (handleCollision) {
        console.log(
          `[Environment] Collision detected between tanks: ${tank.playerId} and ${otherTank.playerId}, ` +
            `distance: ${distance.toFixed(2)}`,
        );
        handleTankCollision(tank, otherTank);
      }
    }
  }

  return collidingTanks;
}

/**
 * Oblicza wektor kierunku odpychania dla czołgu po kolizji
 * @param tank Czołg dla którego obliczamy wektor
 * @param collidingTank Czołg z którym nastąpiła kolizja
 * @returns Wektor kierunku odpychania
 */
export function calculateBounceDirection(tank: Tank, collidingTank: Tank): THREE.Vector3 {
  const pushDirection = new THREE.Vector3().subVectors(tank.mesh.position, collidingTank.mesh.position).normalize();

  // Dodaj małą losowość do kierunku odpychania, aby uniknąć zakleszczenia
  pushDirection.x += (Math.random() - 0.5) * 0.1;
  pushDirection.z += (Math.random() - 0.5) * 0.1;

  return pushDirection;
}
